import React, { useEffect, useState } from 'react';
import { Mail, Users, Activity, Download } from 'lucide-react';
import {
  getNewsletterStats,
  getNewsletterSubscribers,
  getNewsletterExportUrl,
} from '../../services/newsletterApi';

const Reports = () => {
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });

  const loadStats = async () => {
    try {
      const data = await getNewsletterStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading newsletter stats:', err);
      setError('Failed to load newsletter statistics');
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    const loadSubscribers = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (statusFilter) params.set('status', statusFilter);

        const result = await getNewsletterSubscribers(params);
        setSubscribers(result.subscribers);
        setPagination(result.pagination);
      } catch (err) {
        console.error('Error loading subscribers:', err);
        setError('Failed to load newsletter subscribers');
      } finally {
        setLoading(false);
      }
    };

    loadSubscribers();
  }, [page, limit, statusFilter]);

  const handleExport = () => {
    const url = getNewsletterExportUrl(statusFilter);
    window.open(url, '_blank');
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Newsletter Reports
          </h2>
          <p className="text-gray-600">
            Overview of newsletter performance and subscribers
          </p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-900 disabled:opacity-50"
          disabled={loading}
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Total Subscribers
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.totalSubscribers}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Active Subscribers
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.activeSubscribers}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex items-center">
            <div className="p-3 bg-purple-100 rounded-full">
              <Activity className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                New This Month
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.newSubscribersThisMonth}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Subscribers</h3>
            <p className="text-sm text-gray-500">
              List of emails subscribed to your newsletter
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <select
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="bounced">Bounced</option>
            </select>

            <select
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="px-6 py-3 text-sm text-red-600 border-b border-gray-200">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscribed At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && subscribers.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-center text-gray-500 text-sm"
                  >
                    Loading subscribers...
                  </td>
                </tr>
              )}

              {!loading && subscribers.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-8 text-center text-gray-500 text-sm"
                  >
                    No subscribers found.
                  </td>
                </tr>
              )}

              {subscribers.map((subscriber) => (
                <tr key={subscriber._id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {subscriber.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={
                        'inline-flex px-2 py-1 text-xs font-semibold rounded-full ' +
                        (subscriber.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : subscriber.status === 'unsubscribed'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-red-100 text-red-800')
                      }
                    >
                      {subscriber.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscriber.source || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {subscriber.subscribedAt
                      ? new Date(subscriber.subscribedAt).toLocaleString()
                      : subscriber.createdAt
                        ? new Date(subscriber.createdAt).toLocaleString()
                        : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
            <div>
              Showing {(page - 1) * limit + 1}–
              {Math.min(page * limit, pagination.total)} of {pagination.total}{' '}
              subscribers
            </div>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page === 1 || loading}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setPage((prev) => Math.min(pagination.pages, prev + 1))
                }
                disabled={page === pagination.pages || loading}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
